import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Settings as SettingsIcon, Users, Shield, ChevronDown, ChevronUp,
  X, Eye, EyeOff, Lock, Key, Download, Sun, Moon, FileText, Clock, ArrowRight,
} from 'lucide-react';

import Toast from '../components/Toast';
import API_BASE from '../config';

const THEME_KEY = 'martech_theme';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isMobile;
}

const THEMES = {
  light: {
    pageBg: '#ffffff', bg: '#f9fafb', card: '#ffffff', border: '#e5e7eb',
    text: '#111827', subtext: '#6b7280', faint: '#9ca3af',
    brand: '#5B4EE8', brandDark: '#4338ca', brandSoft: '#eef2ff',
    inputBg: '#ffffff', overlay: 'rgba(0,0,0,0.45)',
    success: '#059669', successBg: '#d1fae5', successBorder: '#a7f3d0',
    danger: '#dc2626', dangerBg: '#fee2e2', dangerBorder: '#fecaca',
    violet: '#7c3aed', violetBg: '#ede9fe',
    blue: '#0369a1', blueBg: '#e0f2fe',
    amber: '#b45309', amberBg: '#fef3c7',
    neutralBg: '#f3f4f6', neutralBorder: '#e5e7eb', neutralText: '#374151',
  },
  dark: {
    pageBg: '#0d0e12', bg: '#15161c', card: '#1a1b23', border: '#2b2d38',
    text: '#f3f4f6', subtext: '#9ca3af', faint: '#6b7280',
    brand: '#7C72FF', brandDark: '#6a60e0', brandSoft: '#242440',
    inputBg: '#1f2029', overlay: 'rgba(0,0,0,0.65)',
    success: '#34d399', successBg: 'rgba(16,185,129,0.1)', successBorder: 'rgba(52,211,153,0.2)',
    danger: '#f87171', dangerBg: 'rgba(239,68,68,0.08)', dangerBorder: 'rgba(239,68,68,0.2)',
    violet: '#a78bfa', violetBg: 'rgba(139,92,246,0.1)',
    blue: '#60a5fa', blueBg: 'rgba(59,130,246,0.1)',
    amber: '#fbbf24', amberBg: 'rgba(245,158,11,0.1)',
    neutralBg: '#25262e', neutralBorder: '#2b2d38', neutralText: '#d1d5db',
  },
};

export default function Settings() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [themeName, setThemeName] = useState(() => localStorage.getItem(THEME_KEY) || 'light');
  const C = THEMES[themeName];
  useEffect(() => { localStorage.setItem(THEME_KEY, themeName); }, [themeName]);
  const toggleTheme = () => setThemeName((t) => (t === 'light' ? 'dark' : 'light'));

  const token = localStorage.getItem('martech_token') || localStorage.getItem('token');
  const currentUser = (() => {
    try { return JSON.parse(localStorage.getItem('martech_user') || '{}'); }
    catch { return {}; }
  })();
  const role = currentUser.role || localStorage.getItem('role');
  const userEmail = currentUser.email || 'Unknown';

  const [toast, setToast] = useState(null);
  const [profileCount, setProfileCount] = useState(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const [showChangePw, setShowChangePw] = useState(false);
  const [showReset2FA, setShowReset2FA] = useState(false);

  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [changingPw, setChangingPw] = useState(false);
  const [pwError, setPwError] = useState('');

  const [totpQR, setTotpQR] = useState(null);
  const [resetting2FA, setResetting2FA] = useState(false);

  const showToast = (msg, variant = 'green') => {
    setToast({ msg, variant });
    setTimeout(() => setToast(null), 3100);
  };

  useEffect(() => {
    if (!token) { navigate('/'); return; }
    if (role !== 'admin') return;
    fetch(`${API_BASE}/api/v1/vendor/dashboard`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => { if (d.success) setProfileCount(d.total_profiles); })
      .catch(() => {});
  }, [navigate, role, token]);

  const closeChangePwModal = () => {
    setShowChangePw(false);
    setCurrentPw(''); setNewPw(''); setConfirmPw(''); setPwError(''); setShowPw(false);
  };

  const handleChangePassword = async () => {
    setPwError('');
    if (!currentPw || !newPw || !confirmPw) {
      setPwError('All fields are required');
      return;
    }
    if (newPw.length < 8) {
      setPwError('New password must be at least 8 characters');
      return;
    }
    if (newPw !== confirmPw) {
      setPwError('New password and confirmation do not match');
      return;
    }
    setChangingPw(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/change-password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ current_password: currentPw, new_password: newPw }),
      });
      const d = await res.json();
      if (d.success) {
        showToast('Password updated successfully', 'green');
        closeChangePwModal();
      } else {
        setPwError(d.error || 'Could not change password');
      }
    } catch (e) {
      setPwError('Network error: ' + e.message);
    } finally {
      setChangingPw(false);
    }
  };

  const handle2FAReset = async () => {
    setResetting2FA(true);
    try {
      const userId = currentUser.user_id || currentUser.id;
      const email = currentUser.email || userEmail;
      const res = await fetch(`${API_BASE}/api/auth/totp/setup`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, email }),
      });
      const d = await res.json();
      if (d.qr_code || d.qrCode || d.qrcode) {
        setTotpQR(d.qr_code || d.qrCode || d.qrcode);
        showToast('2FA reset. Scan the QR code below.', 'green');
      } else {
        showToast('2FA reset failed: ' + (d.error || 'unknown error'), 'red');
        setShowReset2FA(false);
      }
    } catch (e) {
      showToast('2FA reset error: ' + e.message, 'red');
      setShowReset2FA(false);
    }
    setResetting2FA(false);
  };

  const exportAllData = () => {
    const payload = {
      exported_at: new Date().toISOString(),
      exported_by: userEmail,
      platform: 'MarTech CDP',
      tenant_id: currentUser.tenant_id || null,
      total_profiles: profileCount,
      note: 'This is a platform configuration snapshot, not a full DPDP data export. For a complete profile-level data export or erasure request, use the Data Rights page.',
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = Object.assign(document.createElement('a'), { href: url, download: 'martech-platform-export.json' });
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
    showToast('Platform data exported successfully', 'green');
  };

  // ---------- shared styles ----------
  const cardStyle = { background: C.card, border: `1px solid ${C.border}`, borderRadius: '16px', padding: isMobile ? '1.25rem' : '1.5rem' };
  const inputStyle = { width: '100%', padding: '0.65rem 0.75rem', borderRadius: '10px', border: `1px solid ${C.border}`, background: C.inputBg, color: C.text, fontSize: '0.9rem', boxSizing: 'border-box', outline: 'none' };
  const labelStyle = { display: 'block', fontSize: '0.7rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', color: C.subtext, marginBottom: '0.4rem' };

  const SectionHeader = ({ icon: Icon, bg, color, title, subtitle }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem' }}>
      <div style={{ width: '2.5rem', height: '2.5rem', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: bg, flexShrink: 0 }}>
        <Icon size={19} color={color} />
      </div>
      <div>
        <h2 style={{ color: C.text, fontWeight: '800', fontSize: '0.95rem', margin: 0 }}>{title}</h2>
        <p style={{ color: C.subtext, fontSize: '0.78rem', margin: '0.1rem 0 0' }}>{subtitle}</p>
      </div>
    </div>
  );

  const Modal = ({ title, children, onClose }) => (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center' }}>
      <div style={{ position: 'absolute', inset: 0, background: C.overlay }} onClick={onClose} />
      <div style={{ position: 'relative', zIndex: 10, background: C.card, border: `1px solid ${C.border}`, borderRadius: isMobile ? '16px 16px 0 0' : '16px', padding: '1.5rem', width: isMobile ? '100%' : '400px', maxWidth: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.1rem' }}>
          <h3 style={{ color: C.text, fontWeight: '800', fontSize: '0.95rem', margin: 0 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.subtext }}>
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );

  if (!token) return null;

  if (role !== 'admin') {
    return (
      <div style={{ background: C.pageBg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: '4rem', height: '4rem', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem', background: C.dangerBg, border: `1px solid ${C.dangerBorder}` }}>
            <Lock size={28} color={C.danger} />
          </div>
          <h2 style={{ color: C.text, fontSize: '1.25rem', fontWeight: '800', marginBottom: '0.5rem' }}>Access denied</h2>
          <p style={{ color: C.subtext, fontSize: '0.9rem' }}>Admin only — you don't have permission to view this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: C.pageBg, minHeight: '100%' }}>
      {toast && <Toast message={toast.msg} variant={toast.variant} onDismiss={() => setToast(null)} />}

      {/* Change Password Modal */}
      {showChangePw && (
        <Modal title="Change Password" onClose={closeChangePwModal}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem', marginBottom: '1.1rem' }}>
            <div>
              <label style={labelStyle}>Current Password</label>
              <input type="password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} placeholder="Current password..." style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>New Password</label>
              <div style={{ position: 'relative' }}>
                <input type={showPw ? 'text' : 'password'} value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="At least 8 characters" style={{ ...inputStyle, paddingRight: '2.25rem' }} />
                <button onClick={() => setShowPw((p) => !p)} style={{ position: 'absolute', right: '0.65rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: C.subtext }}>
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div>
              <label style={labelStyle}>Confirm New Password</label>
              <input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} placeholder="Confirm new password..." style={inputStyle} />
            </div>
            {pwError && (
              <div style={{ background: C.dangerBg, color: C.danger, border: `1px solid ${C.dangerBorder}`, padding: '0.6rem 0.75rem', borderRadius: '8px', fontSize: '0.8rem' }}>{pwError}</div>
            )}
          </div>
          <button
            onClick={handleChangePassword}
            disabled={changingPw}
            style={{ width: '100%', padding: '0.7rem', borderRadius: '10px', border: 'none', color: '#fff', fontWeight: '700', fontSize: '0.9rem', cursor: changingPw ? 'not-allowed' : 'pointer', background: C.brand, opacity: changingPw ? 0.6 : 1 }}
          >
            {changingPw ? 'Updating...' : 'Update Password'}
          </button>
        </Modal>
      )}

      {/* Reset 2FA Modal */}
      {showReset2FA && (
        <Modal title="Reset Two-Factor Authentication" onClose={() => setShowReset2FA(false)}>
          <p style={{ color: C.subtext, fontSize: '0.85rem', marginBottom: '1.25rem', lineHeight: 1.6 }}>
            Are you sure you want to reset 2FA? You'll need to re-scan the QR code on next login.
          </p>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button onClick={() => setShowReset2FA(false)} style={{ flex: 1, padding: '0.65rem', borderRadius: '10px', fontSize: '0.85rem', fontWeight: '600', background: C.neutralBg, border: `1px solid ${C.neutralBorder}`, color: C.subtext, cursor: 'pointer' }}>
              Cancel
            </button>
            <button onClick={handle2FAReset} disabled={resetting2FA} style={{ flex: 1, padding: '0.65rem', borderRadius: '10px', fontSize: '0.85rem', fontWeight: '700', color: '#fff', background: C.brand, border: 'none', cursor: resetting2FA ? 'not-allowed' : 'pointer', opacity: resetting2FA ? 0.6 : 1 }}>
              {resetting2FA ? 'Resetting...' : 'Confirm Reset'}
            </button>
          </div>
          {totpQR && (
            <div style={{ marginTop: '1.25rem', textAlign: 'center' }}>
              <img src={totpQR} alt="QR Code" style={{ maxWidth: 200, margin: '0 auto', borderRadius: '10px' }} />
              <p style={{ color: C.subtext, fontSize: '0.75rem', marginTop: '0.5rem' }}>Scan with Google Authenticator</p>
            </div>
          )}
        </Modal>
      )}

      <div style={{ padding: isMobile ? '1rem' : '2rem 2.5rem', maxWidth: '900px', margin: '0 auto', boxSizing: 'border-box' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ width: '2.5rem', height: '2.5rem', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.violetBg }}>
              <SettingsIcon size={20} color={C.violet} />
            </div>
            <div>
              <h1 style={{ color: C.text, fontSize: '1.4rem', fontWeight: '800', margin: 0, letterSpacing: '-0.01em' }}>Settings</h1>
              <p style={{ color: C.subtext, fontSize: '0.85rem', margin: '0.15rem 0 0' }}>Platform configuration</p>
            </div>
          </div>
          <button
            onClick={toggleTheme}
            title={themeName === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', background: C.card, border: `1px solid ${C.border}`, borderRadius: '10px', cursor: 'pointer' }}
          >
            {themeName === 'light' ? <Moon size={17} color={C.subtext} /> : <Sun size={17} color={C.subtext} />}
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

          {/* ── User Management ── */}
          <div style={cardStyle}>
            <SectionHeader icon={Users} bg={C.violetBg} color={C.violet} title="Your Account" subtitle={profileCount !== null ? `${profileCount} profiles on this tenant` : 'Account details'} />
            <div style={{ border: `1px solid ${C.border}`, borderRadius: '12px', overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile ? '480px' : 'auto' }}>
                  <thead>
                    <tr style={{ background: C.bg, borderBottom: `1px solid ${C.border}` }}>
                      {['Email', 'Role', 'Status', 'Action'].map((h) => (
                        <th key={h} style={{ padding: '0.7rem 1rem', textAlign: 'left', fontSize: '0.68rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', color: C.faint }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: '0.85rem 1rem', fontSize: '0.88rem', fontWeight: '600', color: C.text }}>{userEmail}</td>
                      <td style={{ padding: '0.85rem 1rem' }}>
                        <span style={{ fontSize: '0.7rem', fontWeight: '700', padding: '0.15rem 0.6rem', borderRadius: '999px', background: C.violetBg, color: C.violet }}>{role}</span>
                      </td>
                      <td style={{ padding: '0.85rem 1rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: C.success }} />
                          <span style={{ color: C.success, fontSize: '0.78rem', fontWeight: '600' }}>Active</span>
                        </div>
                      </td>
                      <td style={{ padding: '0.85rem 1rem' }}>
                        <button onClick={() => setShowChangePw(true)} style={{ fontSize: '0.75rem', fontWeight: '600', padding: '0.4rem 0.75rem', borderRadius: '8px', background: C.neutralBg, border: `1px solid ${C.neutralBorder}`, color: C.neutralText, cursor: 'pointer' }}>
                          Change Password
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* ── Two-Factor Authentication ── */}
          <div style={cardStyle}>
            <SectionHeader icon={Shield} bg={C.blueBg} color={C.blue} title="Two-Factor Authentication" subtitle="Secure your admin account with TOTP" />

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', padding: '1rem', borderRadius: '12px', background: C.bg, border: `1px solid ${C.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{ width: '2rem', height: '2rem', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.successBg }}>
                  <Shield size={15} color={C.success} />
                </div>
                <div>
                  <p style={{ color: C.text, fontSize: '0.85rem', fontWeight: '600', margin: 0 }}>2FA Status</p>
                  <p style={{ color: C.subtext, fontSize: '0.75rem', margin: 0 }}>{userEmail}</p>
                </div>
              </div>
              <span style={{ fontSize: '0.68rem', fontWeight: '700', padding: '0.25rem 0.65rem', borderRadius: '999px', background: C.successBg, color: C.success, border: `1px solid ${C.successBorder}`, whiteSpace: 'nowrap' }}>
                Active — Google Authenticator
              </span>
            </div>

            <button onClick={() => setShowReset2FA(true)} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1rem', borderRadius: '10px', fontSize: '0.85rem', fontWeight: '600', background: C.neutralBg, border: `1px solid ${C.neutralBorder}`, color: C.neutralText, cursor: 'pointer', marginBottom: '1rem' }}>
              <Shield size={15} /> Reset 2FA
            </button>

            <button onClick={() => setGuideOpen((g) => !g)} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem', borderRadius: '10px', fontSize: '0.85rem', fontWeight: '600', background: C.bg, border: `1px solid ${C.border}`, color: C.neutralText, cursor: 'pointer' }}>
              <span>2FA Setup Guide</span>
              {guideOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            {guideOpen && (
              <div style={{ marginTop: '0.75rem', borderRadius: '10px', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem', background: C.bg, border: `1px solid ${C.border}` }}>
                {[
                  { step: 1, text: 'Download Google Authenticator from App Store or Google Play' },
                  { step: 2, text: 'Scan the QR code shown on login with the Authenticator app' },
                  { step: 3, text: 'Enter the 6-digit code generated by the app to verify' },
                ].map(({ step, text }) => (
                  <div key={step} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                    <span style={{ width: '1.5rem', height: '1.5rem', borderRadius: '50%', background: C.violetBg, color: C.violet, fontSize: '0.7rem', fontWeight: '800', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '0.1rem' }}>{step}</span>
                    <p style={{ color: C.subtext, fontSize: '0.85rem', margin: 0 }}>{text}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Compliance ── */}
          <div style={cardStyle}>
            <SectionHeader icon={FileText} bg={C.amberBg} color={C.amber} title="Compliance" subtitle="DPDP data rights, retention, and audit visibility" />

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
              <button
                onClick={() => navigate('/data-rights')}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem', borderRadius: '12px', background: C.bg, border: `1px solid ${C.border}`, cursor: 'pointer', textAlign: 'left' }}
              >
                <div>
                  <p style={{ color: C.text, fontSize: '0.85rem', fontWeight: '700', margin: 0 }}>Data Rights Requests</p>
                  <p style={{ color: C.subtext, fontSize: '0.75rem', margin: '0.2rem 0 0' }}>Erasure, portability, correction</p>
                </div>
                <ArrowRight size={16} color={C.faint} />
              </button>
              <button
                onClick={() => navigate('/audit-log')}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem', borderRadius: '12px', background: C.bg, border: `1px solid ${C.border}`, cursor: 'pointer', textAlign: 'left' }}
              >
                <div>
                  <p style={{ color: C.text, fontSize: '0.85rem', fontWeight: '700', margin: 0 }}>Audit Log</p>
                  <p style={{ color: C.subtext, fontSize: '0.75rem', margin: '0.2rem 0 0' }}>Logins, actions, password resets</p>
                </div>
                <ArrowRight size={16} color={C.faint} />
              </button>
            </div>

            <div style={{ padding: '1rem', borderRadius: '12px', background: C.bg, border: `1px solid ${C.border}`, marginBottom: '0.75rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <Clock size={14} color={C.subtext} />
                <p style={{ color: C.subtext, fontSize: '0.7rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0 }}>Data Retention</p>
              </div>
              <p style={{ color: C.text, fontSize: '0.82rem', margin: 0, lineHeight: 1.6 }}>
                DPDP data subject requests (erasure, portability, correction) are processed within 30 days of submission. Audit logs and login records are retained for compliance review; consult your DPA register for client-specific terms.
              </p>
            </div>

            <div style={{ padding: '1rem', borderRadius: '12px', background: C.bg, border: `1px solid ${C.border}` }}>
              <p style={{ color: C.subtext, fontSize: '0.7rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.75rem' }}>Session Info</p>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.6rem' }}>
                {[
                  { label: 'Login Method', value: 'JWT + TOTP', color: C.violet },
                  { label: 'Session Type', value: 'Bearer Token', color: C.neutralText },
                  { label: 'Session Length', value: '30 days', color: C.neutralText },
                  { label: 'Role', value: role || 'admin', color: C.success },
                ].map(({ label, value, color }) => (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.55rem 0.75rem', borderRadius: '8px', background: C.card, border: `1px solid ${C.border}` }}>
                    <span style={{ color: C.subtext, fontSize: '0.78rem' }}>{label}</span>
                    <span style={{ color, fontSize: '0.78rem', fontWeight: '600' }}>{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── Security / Export ── */}
          <div style={cardStyle}>
            <SectionHeader icon={Key} bg={C.amberBg} color={C.amber} title="Security" subtitle="API access and data export" />
            <div style={{ padding: '1rem', borderRadius: '12px', background: C.bg, border: `1px solid ${C.border}` }}>
              <p style={{ color: C.subtext, fontSize: '0.7rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.75rem' }}>Platform Data Export</p>
              <p style={{ color: C.text, fontSize: '0.82rem', marginBottom: '0.9rem', lineHeight: 1.5 }}>
                Export a JSON snapshot of your platform configuration and profile counts. For a full profile-level data export or erasure, use Data Rights Requests above.
              </p>
              <button onClick={exportAllData} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.65rem 1rem', borderRadius: '10px', fontSize: '0.85rem', fontWeight: '700', color: C.amber, background: C.amberBg, border: `1px solid ${C.border}`, cursor: 'pointer' }}>
                <Download size={15} /> Export Platform Data
              </button>
            </div>
          </div>

        </div>
        <div style={{ height: '2.5rem' }} />
      </div>
    </div>
  );
}